import {
  Box,
  CircularProgress,
  Grid,
  Hidden,
  TextField,
  Typography
} from '@mui/material';
import { Link } from 'common/components/Link';
import { FC, useCallback, useEffect } from 'react';
import Reaptcha from '@panalbish/reaptcha-enterprise';
import { loginRoute } from 'authentication/routes';
import { privacyRoute, termsRoute } from 'common/routes';
import { AuthMethod } from 'common/types/graphql-types';
import { useAuthenticateMutation } from 'authentication/mutations/authenticate';
import { useCreateUserMutation } from 'authentication/mutations/createUser';
import { useGraphqlForm } from 'common/hooks/useGraphqlForm';
import { KeyTriggeredButton } from 'common/components/KeyTriggerButton';
import { Key } from 'ts-key-enum';
import getConfig from 'next/config';
import { useMeLazyQuery } from 'user/queries/me';
import { USER_SIGNUP_TERMS_ACKNOWLEDGEMENT } from 'user/constants';
import { useAcknowledgeMutation } from 'user/mutations/acknowledge';
import { useAnalytics } from 'common/hooks/useAnalytics';
import { USER_REGISTERED } from 'authentication/events';
const { publicRuntimeConfig } = getConfig();

export type RegisterInputs = {
  firstName: string;
  lastName: string;
  username: string;
  email: string;
  password: string;
  passwordRepeat: string;
  recaptchaToken: string;
};

const defaultValues: RegisterInputs = {
  firstName: '',
  lastName: '',
  username: '',
  email: '',
  password: '',
  passwordRepeat: '',
  recaptchaToken: ''
};

interface RegisterFormProps {
  onSubmit?: (inputs: RegisterInputs) => Promise<void>;
  onSuccess?: (inputs: RegisterInputs) => void;
  disableLogin?: boolean;
  disabled?: boolean;
  saveButtonText?: string;
}

export const RegisterForm: FC<RegisterFormProps> = ({
  children,
  onSubmit: onSubmitProp,
  onSuccess,
  disableLogin,
  disabled,
  saveButtonText = 'Register'
}) => {
  const analytics = useAnalytics();
  const [authenticate, { data }] = useAuthenticateMutation();
  const [acknowledge] = useAcknowledgeMutation();
  const [createUser, { error, loading }] = useCreateUserMutation();
  const [meQuery] = useMeLazyQuery();
  const { register, handleSubmit, setValue, getValues, errors } =
    useGraphqlForm<RegisterInputs>(error, { defaultValues });

  const verifyRecaptcha = useCallback(
    (token) => {
      setValue('recaptchaToken', token);
    },
    [setValue]
  );

  const expireRecaptcha = useCallback(() => {
    setValue('recaptchaToken', '');
  }, [setValue]);

  useEffect(() => {
    register('recaptchaToken', { required: 'Recaptcha is required' });
  }, []);

  const onSubmit = useCallback(async (variables: RegisterInputs) => {
    try {
      if (onSubmitProp) {
        await onSubmitProp(variables);
      }
      const res = await createUser({
        variables: {
          ...variables,
          //@ts-ignore
          referralCode: window?.Rewardful?.referral
        }
      });
      analytics.capture(USER_REGISTERED, {
        id: res?.data?.createUser?.id
      });
      await authenticate({
        variables: {
          identifier: variables.username,
          secret: variables.password,
          authMethod: AuthMethod.Password
        }
      });
      await acknowledge({
        variables: {
          key: USER_SIGNUP_TERMS_ACKNOWLEDGEMENT
        }
      });
      const ReactPixel = await import('react-facebook-pixel').then(
        (x) => x.default
      );
      ReactPixel.track('CompleteRegistration', { status: true });
      if (onSuccess) {
        onSuccess(variables);
      }
    } catch {}
  }, []);

  useEffect(() => {
    if (data?.authenticate) {
      meQuery();
    }
  }, [data]);

  return (
    <Grid container={true} justifyContent={'center'} spacing={3}>
      <Grid item={true} xs={12} sm={6}>
        <TextField
          {...register('firstName', { required: 'First Name is required' })}
          required={true}
          disabled={disabled}
          helperText={errors?.firstName?.message}
          error={!!errors.firstName}
          fullWidth={true}
          label={'First Name'}
        />
      </Grid>
      <Grid item={true} xs={12} sm={6}>
        <TextField
          {...register('lastName', { required: 'Last Name is required' })}
          required={true}
          disabled={disabled}
          helperText={errors?.lastName?.message}
          error={!!errors.lastName}
          fullWidth={true}
          label={'Last Name'}
        />
      </Grid>
      <Grid item={true} xs={12} sm={5}>
        <TextField
          {...register('username', { required: 'Username is required' })}
          required={true}
          disabled={disabled}
          helperText={errors?.username?.message}
          error={!!errors.username}
          fullWidth={true}
          label={'Username'}
        />
      </Grid>
      <Grid item={true} xs={12} sm={7}>
        <TextField
          {...register('email', { required: 'Email is required' })}
          required={true}
          disabled={disabled}
          helperText={errors?.email?.message}
          error={!!errors.email}
          fullWidth={true}
          label={'Email'}
        />
      </Grid>
      <Grid item={true} xs={12} sm={8}>
        <Box width={'100%'}>
          <TextField
            {...register('password', {
              required: 'Password is required',
              validate: (value) => getValues('passwordRepeat') === value
            })}
            required={true}
            disabled={disabled}
            helperText={errors?.password?.message}
            error={!!errors.password}
            fullWidth={true}
            label={'Password'}
            type={'password'}
          />
        </Box>
        <Box width={'100%'} marginTop={4}>
          <TextField
            {...register('passwordRepeat', {
              required: 'Repeat Password is required',
              validate: (value) =>
                getValues('password') !== value
                  ? 'Passwords do not match'
                  : undefined
            })}
            required={true}
            disabled={disabled}
            helperText={errors?.passwordRepeat?.message}
            error={!!errors.passwordRepeat}
            fullWidth={true}
            label={'Repeat Password'}
            type={'password'}
          />
        </Box>
      </Grid>
      <Grid item={true} xs={12} sm={4}>
        <Hidden mdDown>
          <Reaptcha
            size={'compact'}
            sitekey={publicRuntimeConfig.recaptchaKey}
            onExpire={expireRecaptcha}
            onVerify={verifyRecaptcha}
          />
        </Hidden>
        <Hidden smUp>
          <Reaptcha
            sitekey={publicRuntimeConfig.recaptchaKey}
            onExpire={expireRecaptcha}
            onVerify={verifyRecaptcha}
          />
        </Hidden>
        {errors?.recaptchaToken && (
          <Typography variant={'caption'} color={'error'}>
            {errors?.recaptchaToken?.message}
          </Typography>
        )}
      </Grid>
      {children}
      <Grid item={true} xs={12} sm={7}>
        <Box width={'100%'} marginTop={4}>
          <KeyTriggeredButton
            disabled={disabled}
            fullWidth={true}
            variant={'contained'}
            keyTrigger={Key.Enter}
            color={'primary'}
            onClick={handleSubmit(onSubmit)}
          >
            {loading ? <CircularProgress /> : saveButtonText}
          </KeyTriggeredButton>
        </Box>
      </Grid>
      <Grid item={true} xs={12} sm={9}>
        <Box width={'100%'} textAlign={'center'}>
          <Typography variant={'caption'}>
            By clicking Register, I agree that I have read and accepted the
            Maastr <Link href={termsRoute()}>Terms of Use</Link> and{' '}
            <Link href={privacyRoute()}>Privacy Policy</Link>
          </Typography>
        </Box>
      </Grid>
      {!disableLogin && (
        <Grid item={true} xs={12}>
          <Box width={'100%'} textAlign={'center'}>
            <Typography variant={'caption'}>
              Already have login and password?&nbsp;
              <Link href={loginRoute()}>Sign in</Link>
            </Typography>
          </Box>
        </Grid>
      )}
    </Grid>
  );
};
